/**
 * Credit Control - Dataverse API Integration
 */

import {
  IDataverseOrder,
  IOrderQueryParams,
} from "@/features/orders-management/types";
import { ICommonListApiRes, NetworkMethodEnum } from "@/types";
import { fetcher } from "@/utils/api/dataverse/fetcher";
import { mutator } from "@/utils/api/dataverse/mutator";
import { generateQueryParamsForPagination } from "@/utils/pagination";
import { CREDIT_CONTROL_STATUS_CODE } from "../constants";

export const URL = {
  orders: "/OrderApi",
  approveCreditLimit: "/ApproveCreditLimit",
};

/**
 * Get credit control orders (orders pending credit approval)
 * Filters by statuscode = 736000002
 */
export const getCreditControlOrdersFromDataverse = async (
  queryParams?: IOrderQueryParams
): Promise<ICommonListApiRes<IDataverseOrder>> => {
  // Build base query with pagination
  const query = generateQueryParamsForPagination(queryParams);
  return await fetcher(
    `${URL.orders}${query}&$orderby=createdon desc&$expand=quoteid($select=dogma_region,dogma_projectreference,_dogma_customer_value,dogma_exchangerate;$expand=dogma_Customer_account($select=creditlimit,dogma_availablecredit,name,_dogma_dealerscurrency_value;$expand=dogma_DealersCurrency($select=dogma_name,dogma_currencysymbol,dogma_isocurrencycode,dogma_exchangerate,dogma_vatpercentage)),dogma_DealersCurrency)`,
    queryParams?.pageSize,
  );
};

/**
 * Get the count of orders pending credit approval (statuscode = 736000002)
 */
export const getCreditControlOrdersCountApi = async (region?: number | null): Promise<{ value: { count: number }[] }> => {
  const filter = region !== null && region !== undefined && region !== 0
    ? `statuscode eq ${CREDIT_CONTROL_STATUS_CODE} and quoteid/dogma_region eq ${region}`
    : `statuscode eq ${CREDIT_CONTROL_STATUS_CODE}`;
  return await fetcher(`${URL.orders}?$apply=aggregate($count as count)&$filter=${filter}`);
};

/**
 * Approve or reject credit limit for an order
 * @param data - Object containing orderId, newCreditLimit (optional), status ("approve" or "reject"), and notes (optional)
 */
export interface IApproveCreditLimitRequest {
  orderId: string;
  newCreditLimit?: number | null;
  status: "approve" | "reject";
  notes?: string;
}

export interface IApproveCreditLimitResponse {
  success: boolean;
  message: string;
  orderId: string;
}

export const approveCreditLimit = async (
  data: IApproveCreditLimitRequest
): Promise<IApproveCreditLimitResponse> => {
  return await mutator(NetworkMethodEnum.POST, URL.approveCreditLimit, data);
};
